import { ComponentPropsWithoutRef, ReactNode, createContext, useContext, useRef } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { useCalendarDragState, useCalendarGridStore, useScrollDeepLinkedEventIntoView } from '@frontend/calendar-grid';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { rgba } from '@frontend/design-system';
import { useCalendarDeepLinkParams } from '../../../../hooks/calendar/use-calendar-deep-link-params';
import { PersonStatusIndicator } from '../../../schedule-calendar-components/appointment-event-card/person-status-indicator';
import { getNonAttendedEventStatusIconName } from '../../calendar-view-v3/helper';
import { EVENT_CARD_COLOR_LINE_WIDTH } from '../../constants';
import { orNone } from '../../utils';

type GridEventCardContext = {
  id: string;
  primaryColor: string;
  backgroundColor: string;
  isNonAttendedEvent: boolean;
  isSelected: boolean;
  isRequest: boolean;
};

const GridEventCardContext = createContext<GridEventCardContext | null>(null);

const useGridEventCardContext = () => {
  const context = useContext(GridEventCardContext);
  if (!context) {
    throw new Error('useGridEventCardContext must be used within GridEventCard');
  }
  return context;
};

type GridEventCardProps = {
  children: ReactNode;
  id: string;
  primaryColor?: string;
  backgroundColor?: string;
  isNonAttendedEvent?: boolean;
  isRequest?: boolean;
};

const GridEventCardProvider = ({
  children,
  id,
  primaryColor = theme.colors.primary50,
  backgroundColor = theme.colors.white,
  isNonAttendedEvent = false,
  isRequest = false,
}: GridEventCardProps) => {
  const selectedEventIds = useCalendarGridStore((s) => s.selectedEventIds);
  const isSelected = !!selectedEventIds?.includes(id);

  return (
    <GridEventCardContext.Provider
      value={{
        id,
        primaryColor,
        backgroundColor,
        isNonAttendedEvent,
        isSelected,
        isRequest,
      }}
    >
      {children}
    </GridEventCardContext.Provider>
  );
};

// Sub-components
const EventColoredLine = () => {
  const { primaryColor, isRequest } = useGridEventCardContext();

  return (
    <div
      style={{
        position: 'absolute',
        background: primaryColor,
        left: isRequest ? '2px' : 0,
        top: 0,
        width: EVENT_CARD_COLOR_LINE_WIDTH,
        height: '100%',
      }}
    />
  );
};

const PersonStatusBar = ({ personStatus }: { personStatus?: string }) => {
  return <PersonStatusIndicator personStatus={personStatus} height={4} borderRadius='small' isExpanded={false} />;
};

const ShellWrapper = ({ children, ...rest }: ComponentPropsWithoutRef<'div'>) => {
  const { id, isNonAttendedEvent, isSelected } = useGridEventCardContext();
  const eventCardRef = useRef<HTMLDivElement>(null);

  const {
    isEventCardOverlay,
    eventCardOverlayStyle,
    isEventDropOverlay,
    eventDropOverlayStyle,
    isEventDragOverlay,
    eventDragOverlayStyle,
  } = useCalendarDragState();

  // Read deep-link state is populated by CalendarGridProvider
  const { isDeepLinkedEvent } = useCalendarDeepLinkParams([id]);

  // Auto-scroll to this card when it is the deep-linked focused event
  useScrollDeepLinkedEventIntoView({ isDeepLinkedEvent, elementRef: eventCardRef });

  return (
    <div
      ref={eventCardRef}
      css={[
        css`
          border-radius: ${theme.borderRadius.small};
          box-shadow: ${theme.shadows.light};
          transition: box-shadow 0.2s ease-in-out;
          &:hover {
            box-shadow: ${theme.shadows.floating};
          }
          height: 100%;
        `,
        isNonAttendedEvent &&
          css({
            background: rgba(theme.colors.critical20, 0.2),
            border: `1px dashed ${theme.colors.critical50}`,
            borderRadius: theme.borderRadius.small,
            transition: 'all 0.3s cubic-bezier(0.7, -0.4, 0.4, 1.4)',
            height: '100%',

            [`${orNone((isSelected && '&') || '&:hover, &:focus-visible')}`]: {
              ...nonAttendedEventHoverStyle,
            },
          }),
        isEventCardOverlay && eventCardOverlayStyle,
        isEventDropOverlay && eventDropOverlayStyle,
        isEventDragOverlay && eventDragOverlayStyle,
      ]}
      {...rest}
    >
      {children}
    </div>
  );
};

const Shell = ({ children, ...rest }: ComponentPropsWithoutRef<'article'>) => {
  const { backgroundColor, isNonAttendedEvent, isSelected, isRequest } = useGridEventCardContext();

  return (
    <article
      css={[
        css`
          padding: ${theme.spacing(0.5, 0.5, 0, 1.5)};
          position: relative;
          overflow: hidden;
          outline: none;
          background: ${backgroundColor};
          height: 100%;
        `,
        isRequest &&
          css`
            opacity: 1;
            background: white;
            background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${encodeURIComponent(
              theme.colors.primary50
            )}' stroke-width='4' stroke-dasharray='10%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
            &:hover,
            &:active {
              background-color: ${backgroundColor};
            }
          `,
        isNonAttendedEvent &&
          css`
            opacity: ${isSelected ? 0.5 : 0};
            transition: opacity 0.3s ease-in-out;
            background: transparent;
          `,
      ]}
      {...rest}
    >
      {children}
    </article>
  );
};

// V3-specific components for non-attended events
const NonAttendedEventStatusIcon = ({ eventStatus }: { eventStatus?: EventStatus }) => {
  const { isSelected } = useGridEventCardContext();
  const eventStatusIconName = getNonAttendedEventStatusIconName(eventStatus);
  if (!eventStatusIconName || isSelected) return null;

  return (
    <Icon
      name={eventStatusIconName}
      color='error'
      size={16}
      css={{
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        transition: 'opacity 0.2s ease-in-out',
      }}
    />
  );
};

const nonAttendedEventHoverStyle = css({
  background: theme.colors.white,
  borderColor: 'transparent',
  border: `0px`,
  '& > article': {
    opacity: 0.5,
  },
  '& > svg': {
    opacity: 0,
  },
});

// Export compound component
export const GridEventCard = Object.assign(GridEventCardProvider, {
  EventColoredLine,
  PersonStatusBar,
  Shell,
  ShellWrapper,
  NonAttendedEventStatusIcon,
});
